import { useEffect, useRef, useState }   from 'react'
import Select                           from 'react-select'
import { colourStyles }                 from '../../utils/stylesUtils'

const NONE_OPTION = { label: 'Nenhum', value: null }

// Selects menores para caberem dentro do card. O menu fica dentro da <li>
// (sem portal) para o clique nele não contar como "clique fora".
const compactStyles = (baseStyles = {}) => ({
    ...baseStyles,
    control:            (styles, state) => ({ ...(baseStyles.control ? baseStyles.control(styles, state) : styles), minHeight: 34 }),
    valueContainer:     (styles) => ({ ...styles, padding: '0 6px' }),
    dropdownIndicator:  (styles) => ({ ...styles, padding: 4 }),
    clearIndicator:     (styles) => ({ ...styles, padding: 4 }),
    indicatorSeparator: ()       => ({ display: 'none' }),
    menu:               (styles) => ({ ...styles, zIndex: 50 }),
})

const FAROL_FIELDS = ['farolRestrictionId', 'categoryId', 'productId', 'clientId', 'hooperId']

const findOption = (options, id) => options.find(option => option.value === id) ?? null

const toFarolData = (values) => ({
    farolRestrictionId: values.farolRestriction?.value ?? null,
    categoryId:         values.category?.value         ?? null,
    productId:          values.product?.value          ?? null,
    clientId:           values.client?.value           ?? null,
    hooperId:           values.hooper?.value           ?? null,
    description:        values.description.trim()      || null,
})

// Linha da lista de faróis em modo de edição. Clique fora salva, Esc cancela.
// "farol" nulo significa uma linha nova (criada pelo botão Adicionar).
const FarolListItemEditor = ({ farol, options, onSave, onCancel, onDelete }) => {

    const isNew = !farol

    const [ _values, _setValues ] = useState(() => ({
        farolRestriction:   findOption(options.restrictions, farol?.farolRestrictionId),
        category:           findOption(options.categories,   farol?.categoryId),
        product:            findOption(options.products,     farol?.productId),
        client:             findOption(options.clients,      farol?.clientId),
        hooper:             findOption(options.hoopers,      farol?.hooperId),
        description:        farol?.description ?? '',
    }))

    const [ _error,    _setError  ] = useState(null)
    const [ _isSaving, _setSaving ] = useState(false)

    const _rowRef    = useRef(null)
    const _commitRef = useRef(null)

    const _setField = (field) => (value) => _setValues(current => ({ ...current, [field]: value }))

    const _commit = async () => {

        if (_isSaving) return

        const farolData = toFarolData(_values)

        if (isNew && Object.values(farolData).every(value => value === null)) return onCancel()

        const isUnchanged = !isNew
            && FAROL_FIELDS.every(field => farolData[field] === (farol[field] ?? null))
            && farolData.description === (farol.description || null)

        if (isUnchanged) return onCancel()

        if (!farolData.farolRestrictionId || !farolData.categoryId) {
            return _setError('Restrição e categoria são obrigatórias.')
        }

        _setSaving(true)

        const error = await onSave(farolData)

        if (error) {
            _setError(error)
            _setSaving(false)
        }
    }

    // O listener é registrado uma vez; a ref sempre aponta para o _commit com os valores atuais.
    _commitRef.current = _commit

    useEffect(() => {

        const handleMouseDown = (event) => {
            if (_rowRef.current && !_rowRef.current.contains(event.target)) _commitRef.current()
        }

        document.addEventListener('mousedown', handleMouseDown)

        return () => document.removeEventListener('mousedown', handleMouseDown)

    }, [])

    const _handleKeyDown = (event) => {
        // Esc com um menu aberto só fecha o menu (o react-select marca defaultPrevented).
        if (event.key === 'Escape' && !event.defaultPrevented) onCancel()
    }

    const _handleDelete = () => {
        if (window.confirm('Excluir este item do farol?')) onDelete()
    }

    return (

        <li
            ref={_rowRef}
            onKeyDown={_handleKeyDown}
            className="flex flex-col gap-1 p-1 my-1 border border-blue-500 rounded-[5px] text-[0.85rem]"
        >

            {/* Selects lado a lado; num card estreito eles quebram de linha em vez de espremer. */}
            <div className="flex flex-wrap gap-1 [&>*]:flex-1 [&>*]:min-w-[110px]">

                <Select
                    autoFocus={isNew}
                    placeholder="Restrição *"
                    options={options.restrictions}
                    value={_values.farolRestriction}
                    onChange={_setField('farolRestriction')}
                    styles={compactStyles(colourStyles)}
                    isSearchable={false}
                />

                <Select
                    placeholder="Categoria *"
                    options={options.categories}
                    value={_values.category}
                    onChange={_setField('category')}
                    styles={compactStyles()}
                />

                <Select
                    placeholder="Produto"
                    options={[NONE_OPTION, ...options.products]}
                    value={_values.product}
                    onChange={_setField('product')}
                    styles={compactStyles()}
                />

                <Select
                    placeholder="Cliente"
                    options={[NONE_OPTION, ...options.clients]}
                    value={_values.client}
                    onChange={_setField('client')}
                    styles={compactStyles()}
                />

                <Select
                    placeholder={options.hoopers.length === 0 ? 'Sem moegas' : 'Moega'}
                    options={[NONE_OPTION, ...options.hoopers]}
                    value={_values.hooper}
                    onChange={_setField('hooper')}
                    styles={compactStyles()}
                    isDisabled={options.hoopers.length === 0}
                />

            </div>

            <textarea
                rows="2"
                placeholder="Descrição"
                value={_values.description}
                onChange={(event) => _setField('description')(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); _commit() } }}
                className="w-full border border-gray-300 rounded-[4px] px-2 py-1 resize-none focus:outline-none focus:border-blue-500"
            />

            {_error && <p className="text-red-500">{_error}</p>}

            <div className="flex justify-between items-center gap-2">

                <span className="text-[0.72rem] text-[darkgray]">
                    {_isSaving ? 'Salvando...' : 'Clique fora ou Enter para salvar · Esc cancela'}
                </span>

                {!isNew && (
                    <button
                        type="button"
                        onClick={_handleDelete}
                        className="flex items-center gap-1 shrink-0 px-3 h-[30px] rounded-[4px] border border-red-500 text-red-500 text-[0.8rem] font-medium hover:bg-red-500 hover:text-white transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-[14px] h-[14px]" aria-hidden="true">
                            <path d="M3 6h18" />
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        </svg>
                        Excluir
                    </button>
                )}

            </div>

        </li>
    )
}

export default FarolListItemEditor
